import { CalendarDays, CircleHelp } from 'lucide-react';
import { formatMoney, type CashForecastView } from '../api';
import { useDeskData, formatDay } from './desk';
import '../pages/cash.css';

export type CashWindowKey = 'thisWeek' | 'nextWeek' | 'next30Days';
export const cashWindowLabels: Record<CashWindowKey, string> = {
  thisWeek: 'This week', nextWeek: 'Next week', next30Days: 'Next 30 days',
};
type LikelyValue = CashForecastView['likely'][CashWindowKey];

function MoneyLines({ values, empty }: { values: { minor: string; currency: string }[]; empty: string }) {
  if (!values.length) return <span className="cash-none">{empty}</span>;
  return <span className="cash-money">{values.map(value => <span key={value.currency}>{formatMoney(value.minor, value.currency)}</span>)}</span>;
}

const noEstimate = (likely: LikelyValue) => !likely.count && !likely.amounts.length;

function LikelyLines({ likely, label, quiet }: { likely: LikelyValue; label: string; quiet: boolean }) {
  // Nothing to project: one quiet line, or nothing when the page already says it once for every window.
  if (noEstimate(likely)) return quiet ? null : <p className="cash-estimate-empty">No estimate yet: too little payment history.</p>;
  return <div className="cash-estimate">
    <span className="cash-estimate-tag">Estimate</span>
    <MoneyLines values={likely.amounts} empty="No estimate yet" />
    <small>{`${likely.count} ${likely.count === 1 ? 'invoice' : 'invoices'} across ${likely.customers} ${likely.customers === 1 ? 'customer' : 'customers'}`}</small>
    <details className="cash-basis">
      <summary><CircleHelp size={12} aria-hidden />How this estimate works</summary>
      <p>{likely.method}</p>
      {likely.basis.length > 0 && <ul>{likely.basis.map(entry => <li key={entry.customerId}>{entry.label}</li>)}</ul>}
      {likely.excluded.invoices > 0 && <p className="cash-excluded">{likely.excluded.invoices}
        {` ${likely.excluded.invoices === 1 ? 'invoice has' : 'invoices have'} too little payment history and `}
        {likely.excluded.invoices === 1 ? 'is' : 'are'} left out.</p>}
      <p className="cash-excluded" aria-label={`Basis for ${label}`}>Promised and estimated money are never added together.</p>
    </details>
  </div>;
}

export function CashWindows({ view }: { view: CashForecastView }) {
  const keys = Object.keys(cashWindowLabels) as CashWindowKey[];
  const none = keys.every(key => noEstimate(view.likely[key]));
  return <><div className="cash-windows">
    {keys.map(key => {
      const window = view.windows[key]; const likely = view.likely[key];
      return <section className="cash-window" key={key} aria-labelledby={`cash-window-${key}`}>
        <header><h3 id={`cash-window-${key}`}>{cashWindowLabels[key]}</h3>
          <span>{window.from === window.to ? formatDay(window.from, view.asOf) : `${formatDay(window.from, view.asOf)} – ${formatDay(window.to, view.asOf)}`}</span></header>
        <div className="cash-promised">
          <span className="cash-label">Promised</span>
          {window.promised.length ? <MoneyLines values={window.promised} empty="" /> : <span className="cash-money cash-zero">$0</span>}
          <small>{window.promisedCount
            ? `${window.promisedCount} ${window.promisedCount === 1 ? 'promise' : 'promises'} across ${window.promisedCustomers} ${window.promisedCustomers === 1 ? 'customer' : 'customers'}`
            : 'No promise falls in this window.'}</small>
        </div>
        <LikelyLines likely={likely} label={cashWindowLabels[key]} quiet={none} />
        {window.collected.length > 0 && <div className="cash-collected">
          <span className="cash-label">Collected</span><MoneyLines values={window.collected} empty="" />
        </div>}
      </section>;
    })}
  </div>
  {none && <p className="cash-estimate-empty cash-estimate-page">No estimate from payment habits yet: customers need more paid
    invoices on record. Promised money above is exact.</p>}
  {view.likely.thisWeek.pastHabit.count > 0 && <p className="cash-past-habit">
    Not in these estimates: <MoneyLines values={view.likely.thisWeek.pastHabit.amounts} empty="" /> across
    {` ${view.likely.thisWeek.pastHabit.count} ${view.likely.thisWeek.pastHabit.count === 1 ? 'invoice' : 'invoices'}`} already later than
    their customers usually pay. <a href="#collections?status=all">Chase them in Collections</a></p>}</>;
}

export function BrokenThisWeek({ view }: { view: CashForecastView }) {
  return <section className="cash-broken" aria-labelledby="cash-broken-title">
    <h3 id="cash-broken-title">Broken this week</h3>
    {view.brokenThisWeek.length ? <ol>
      {view.brokenThisWeek.map(promise => <li key={promise.promiseId}>
        <a href={`#customers/${encodeURIComponent(promise.customerId)}`}>{promise.customerName}</a>
        <span className="cash-broken-amount">{formatMoney(promise.amount.minor, promise.amount.currency)}</span>
        <small>Promised {formatDay(promise.promisedOn, view.asOf)}, broke {formatDay(promise.brokenOn, view.asOf)}. {promise.nextAction}</small>
      </li>)}
    </ol> : <p className="cash-none">No promise broke this week.</p>}
  </section>;
}

/** The Home card: the two kinds of expected cash stay separate, and the calendar link opens the full month. */
export function CashExpectedCard() {
  const { data: view, error } = useDeskData<CashForecastView>('/app-api/cash/forecast');
  return <section className="cash-home" aria-labelledby="cash-home-title">
    <header className="cash-home-head">
      <div><h2 id="cash-home-title" className="home-section-title">Cash expected</h2>
        <p>Promised money and likely money are shown side by side and never added into one number.</p></div>
      <a className="cash-home-link" href="#cash"><CalendarDays size={14} aria-hidden />Calendar</a>
    </header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {view ? <><CashWindows view={view} /><BrokenThisWeek view={view} /></>
      : <p className="cash-none" role="status">Reading promises and payment history…</p>}
  </section>;
}
